<template>
  <figure class="trend" @mouseleave="hover = null">
    <!-- 값 눈금 -->
    <div class="trend__axis-y">
      <span v-for="(line, index) in gridLines" :key="`ty-${index}`" :style="{ top: `${(line.y / H) * 100}%` }">
        {{ shortNumber(line.value) }}
      </span>
    </div>

    <!-- 그래프 영역. 툴팁·날짜 눈금이 이 박스를 기준으로 정렬된다. -->
    <div class="trend__plot">
      <svg
        ref="svgRef"
        class="trend__svg"
        :viewBox="`0 0 ${W} ${H}`"
        preserveAspectRatio="none"
        role="img"
        :aria-label="ariaLabel"
        @mousemove="onMove"
      >
        <defs>
          <linearGradient :id="gradientId" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" :stop-color="color" stop-opacity="0.26" />
            <stop offset="100%" :stop-color="color" stop-opacity="0" />
          </linearGradient>
        </defs>

        <!-- 가로 기준선 -->
        <line
          v-for="(line, index) in gridLines"
          :key="`grid-${index}`"
          class="trend__grid"
          :x1="PAD.l"
          :x2="W - PAD.r"
          :y1="line.y"
          :y2="line.y"
        />

        <path v-if="areaPath" class="trend__area" :d="areaPath" :fill="`url(#${gradientId})`" />
        <path v-if="linePath" class="trend__line" :d="linePath" :stroke="color" />

        <template v-if="hover">
          <line class="trend__cursor" :x1="hover.x" :x2="hover.x" :y1="PAD.t" :y2="H - PAD.b" />
          <circle class="trend__dot" :cx="hover.x" :cy="hover.y" :r="4.5" :stroke="color" />
        </template>
      </svg>

      <!-- 날짜 눈금 -->
      <div class="trend__axis-x">
        <span v-for="tick in xTicks" :key="tick.label" :style="{ left: `${(tick.x / W) * 100}%` }">
          {{ tick.label }}
        </span>
      </div>

      <div
        v-if="hover"
        class="trend__tip"
        :style="{ left: `${(hover.x / W) * 100}%`, top: `${(hover.y / H) * 100}%` }"
      >
        <strong>{{ hover.label }}</strong>
        <span>{{ format(hover.value) }}</span>
      </div>
    </div>
  </figure>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

export interface TrendPoint {
  label: string
  value: number
}

const props = withDefaults(
  defineProps<{
    points: TrendPoint[]
    color?: string
    unit?: string
    ariaLabel?: string
  }>(),
  {
    color: '#1a5fa8',
    unit: '원',
    ariaLabel: '기간별 추이 그래프',
  },
)

/* viewBox 좌표계. 실제 크기는 CSS가 정한다. */
const W = 720
const H = 240
const PAD = { t: 16, r: 8, b: 26, l: 8 }

const svgRef = ref<SVGSVGElement | null>(null)
const hover = ref<{ x: number; y: number; label: string; value: number } | null>(null)

const gradientId = `trend-grad-${Math.random().toString(36).slice(2, 8)}`

const maxValue = computed(() => {
  const max = Math.max(...props.points.map((p) => p.value), 0)
  if (max <= 0) return 1
  /* 눈금이 어중간하게 끊기지 않도록 위쪽을 올림한다 */
  const digits = Math.pow(10, Math.floor(Math.log10(max)))
  return Math.ceil((max * 1.1) / digits) * digits
})

const coords = computed(() => {
  const count = props.points.length
  if (!count) return []
  const innerW = W - PAD.l - PAD.r
  const innerH = H - PAD.t - PAD.b

  return props.points.map((point, index) => ({
    ...point,
    x: PAD.l + (count === 1 ? innerW / 2 : (innerW * index) / (count - 1)),
    y: PAD.t + innerH - (point.value / maxValue.value) * innerH,
  }))
})

const linePath = computed(() => {
  const list = coords.value
  if (list.length < 2) return ''
  /* 완만한 곡선 — 인접 점의 중간을 제어점으로 삼는 Catmull-Rom 근사 */
  return list.reduce((path, point, index) => {
    if (index === 0) return `M ${point.x} ${point.y}`
    const prev = list[index - 1]
    const cx = (prev.x + point.x) / 2
    return `${path} C ${cx} ${prev.y}, ${cx} ${point.y}, ${point.x} ${point.y}`
  }, '')
})

const areaPath = computed(() => {
  if (!linePath.value) return ''
  const list = coords.value
  const first = list[0]
  const last = list[list.length - 1]
  return `${linePath.value} L ${last.x} ${H - PAD.b} L ${first.x} ${H - PAD.b} Z`
})

const gridLines = computed(() => {
  const steps = 4
  const innerH = H - PAD.t - PAD.b
  return Array.from({ length: steps + 1 }, (_, index) => ({
    y: PAD.t + (innerH * index) / steps,
    value: (maxValue.value * (steps - index)) / steps,
  }))
})

const xTicks = computed(() => {
  const list = coords.value
  if (!list.length) return []
  const wanted = Math.min(6, list.length)
  const step = Math.max(1, Math.floor((list.length - 1) / (wanted - 1 || 1)))
  const ticks: Array<{ x: number; label: string }> = []
  for (let index = 0; index < list.length; index += step) {
    ticks.push({ x: list[index].x, label: list[index].label })
  }
  const last = list[list.length - 1]
  if (ticks[ticks.length - 1]?.label !== last.label) {
    ticks.push({ x: last.x, label: last.label })
  }
  return ticks
})

function onMove(event: MouseEvent) {
  const list = coords.value
  if (!list.length || !svgRef.value) return
  const rect = svgRef.value.getBoundingClientRect()
  const ratio = (event.clientX - rect.left) / rect.width
  const index = Math.round(ratio * (list.length - 1))
  const point = list[Math.min(Math.max(index, 0), list.length - 1)]
  hover.value = { x: point.x, y: point.y, label: point.label, value: point.value }
}

function shortNumber(value: number): string {
  if (value >= 100000000) return `${(value / 100000000).toFixed(1)}억`
  if (value >= 10000) return `${Math.round(value / 10000)}만`
  return Math.round(value).toLocaleString('ko-KR')
}

function format(value: number): string {
  return `${value.toLocaleString('ko-KR')}${props.unit}`
}
</script>

<style lang="scss" src="./SellerTrendChart.scss"></style>
